import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import * as S from './SearchDropdown.styles';
import { IAppState } from '../core/redux/IAppState';
import { useSelector } from 'react-redux';
import { getResource } from '../language/resources/getResource';

type FieldElement = HTMLDivElement | HTMLInputElement;

export type BehaviorAPI = {
  text: string;
  setText: (next: string) => void;
  setKeyError: (msg: string | null) => void;
  fieldRef: React.RefObject<FieldElement | null>;
  getPrevNonWsChar: (s: string, cursorIndex: number) => string | null;
  getNextNonWsChar: (s: string, cursorIndex: number) => string | null;
};

export type FieldBehavior<ValidPayload = unknown> = {
  fieldKind: 'textarea' | 'input';
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  maxLengthAttr?: number;
  sanitizeOnChange?: (text: string) => string;
  beforeInput?: (e: React.FormEvent<FieldElement>, api: BehaviorAPI) => void;
  onPaste?: (e: React.ClipboardEvent<FieldElement>, api: BehaviorAPI) => void;
  onDrop?: (e: React.DragEvent<FieldElement>, api: BehaviorAPI) => void;
  onKeyDown?: (e: React.KeyboardEvent<FieldElement>, api: BehaviorAPI) => void;
  finalizeOnBlur?: (text: string) => string;
  normalizeOnClose?: (text: string) => string;
  validate?: (text: string) => {
    error: string | null;
    validPayload?: ValidPayload;
    invalidRanges?: { start: number; end: number }[] | null;
  };
  caretToEndOnOpen?: boolean;
};

export type SearchDropdownBaseProps<ValidPayload = unknown> = {
  label: string;
  value?: string | null | undefined;
  defaultValue?: string;
  onChange?: (value: string) => void;

  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;

  controlWidth?: number | string;
  controlMaxWidth?: number | string;
  controlHeight?: number | string;
  iconSize?: number | string;

  panelMinWidth?: number | string;
  panelMaxWidth?: number | string;
  panelMaxHeight?: number;

  rows?: number;          // applied to CE
  autoGrow?: boolean;     // CE: true => min-height, false => fixed height

  closeOnEnter?: boolean;
  behavior: FieldBehavior<ValidPayload>;
  onValidPayload?: (payload: ValidPayload) => void;
  onSearch?: (text: string) => void;

  showToolbarClear?: boolean;
  clearLabel?: string;
};

/** Escape for invalid span wrapping */
function escapeHtml(s: string) {
  if (!s) return '';
  return s.replace(/[&<>]/g, ch => (ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : '&gt;'));
}

/** Build HTML with invalid ranges wrapped */
function renderWithInvalids(raw: string, ranges: { start: number; end: number }[] | null | undefined) {
  const text = raw ?? '';
  if (!ranges || ranges.length === 0) return escapeHtml(text).replace(/\n/g, '<br/>');

  const sorted = [...ranges]
    .filter(r => r && r.start >= 0 && r.end > r.start && r.end <= text.length)
    .sort((a, b) => a.start - b.start);

  const parts: string[] = [];
  let cursor = 0;
  for (const r of sorted) {
    const start = Math.max(cursor, r.start);
    const end = Math.max(start, r.end);
    if (start > cursor) parts.push(escapeHtml(text.slice(cursor, start)));
    if (end > start) parts.push(`<span class="invalid">${escapeHtml(text.slice(start, end))}</span>`);
    cursor = Math.max(cursor, end);
  }
  if (cursor < text.length) parts.push(escapeHtml(text.slice(cursor)));
  return parts.join('').replace(/\n/g, '<br/>');
}

function getCEText(el: HTMLDivElement) {
  return el.textContent ?? '';
}

function setCaretToEnd(el: HTMLDivElement) {
  try {
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  } catch {}
}

function getSelectionIndex(el: HTMLDivElement): number {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return 0;
  const range = sel.getRangeAt(0);
  if (!el.contains(range.startContainer)) return 0;
  const r = document.createRange();
  r.selectNodeContents(el);
  r.setEnd(range.startContainer, range.startOffset);
  return r.toString().length;
}

function setCaretAtIndex(el: HTMLDivElement, index: number) {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode() as Text | null;
  let remaining = index;
  while (node) {
    const len = node.nodeValue?.length ?? 0;
    if (remaining <= len) {
      const range = document.createRange();
      range.setStart(node, Math.max(0, remaining));
      range.collapse(true);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      return;
    }
    remaining -= len;
    node = walker.nextNode() as Text | null;
  }
  setCaretToEnd(el);
}

export function SearchDropdownBase<ValidPayload = unknown>({
  label,
  value,
  defaultValue,
  onChange,
  searchPlaceholder,
  className = '',
  disabled = false,
  onOpenChange,
  controlWidth,
  controlMaxWidth,
  controlHeight,
  iconSize = 24,
  panelMinWidth,
  panelMaxWidth,
  panelMaxHeight,
  rows = 2,
  autoGrow = true,
  closeOnEnter = true,
  behavior,
  onValidPayload,
  onSearch,
  showToolbarClear = true,
  clearLabel = 'Clear',
}: SearchDropdownBaseProps<ValidPayload>) {
  const resources = useSelector((state: IAppState) => state.locale);

  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue ?? '');
  const [triggerWidthPx, setTriggerWidthPx] = useState<number>(0);

  const [error, setError] = useState<string | null>(null);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [invalidRanges, setInvalidRanges] = useState<{ start: number; end: number }[] | null>(null);

  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const fieldRef = useRef<FieldElement | null>(null);
  const helperRef = useRef<HTMLDivElement | null>(null);

  const composingRef = useRef(false);
  const closingRef = useRef(false);
  const popupId = useId();

  const text = (value ?? internalValue ?? '') as string;

  // CE height from rows + max height on CE (scrollbar in CE)
  // 1.4em line-height and 6px top/bottom padding => +12px
  const ceMinHeightCss = useMemo(() => {
    const r = Math.max(1, Number.isFinite(rows as number) ? (rows as number) : 1);
    return `calc(${r} * 1.4em + 12px)`;
  }, [rows]);

  const editableStyle = useMemo<React.CSSProperties>(() => {
    const style: React.CSSProperties = { overflow: 'auto' };
    if (autoGrow !== false) {
      style.minHeight = ceMinHeightCss;        // grows naturally
    } else {
      style.height = ceMinHeightCss;           // fixed rows height
      style.minHeight = ceMinHeightCss;
      style.maxHeight = ceMinHeightCss;
    }
    // If caller sets panelMaxHeight, cap CE with it (so CE gets the scrollbar)
    if (typeof panelMaxHeight === 'number') {
      style.maxHeight = `${panelMaxHeight}px`;
    }
    return style;
  }, [autoGrow, ceMinHeightCss, panelMaxHeight]);

  const api: BehaviorAPI = {
    text,
    setText: (next: string) => {
      const sanitized = behavior.sanitizeOnChange ? behavior.sanitizeOnChange(next) : next;
      onChange ? onChange(sanitized) : setInternalValue(sanitized);
    },
    setKeyError,
    fieldRef,
    getPrevNonWsChar: (s: string, cursorIndex: number) => {
      for (let i = cursorIndex - 1; i >= 0; i--) {
        const ch = s[i];
        if (ch && !/\s/.test(ch)) return ch;
      }
      return null;
    },
    getNextNonWsChar: (s: string, cursorIndex: number) => {
      for (let i = cursorIndex; i < s.length; i++) {
        const ch = s[i];
        if (ch && !/\s/.test(ch)) return ch;
      }
      return null;
    },
  };

  const hasValidationErrors = (res?: { error?: string | null; invalidRanges?: Array<any> | null }) =>
    !!res?.error || (Array.isArray(res?.invalidRanges) && res.invalidRanges.length > 0);

  const surfaceValidation = useCallback((res?: { error?: string | null; invalidRanges?: Array<any> | null }) => {
    setError(res?.error ?? null);
    setInvalidRanges(res?.invalidRanges ?? null);
    setKeyError(null);
    requestAnimationFrame(() => {
      (fieldRef.current as any)?.focus?.();
      helperRef.current?.scrollIntoView({ block: 'nearest' });
    });
  }, []);

  const normalizeOnCloseIfNeeded = useCallback((liveVal: string) => {
    if (typeof behavior.normalizeOnClose !== 'function') return;
    closingRef.current = true;
    const normalized = behavior.normalizeOnClose(liveVal);
    if (normalized !== liveVal) {
      onChange ? onChange(normalized) : setInternalValue(normalized);
    }
    setKeyError(null);
    setError(null);
    setInvalidRanges(null);
  }, [behavior, onChange]);

  const focusOnOpen = useCallback(() => {
    requestAnimationFrame(() => {
      const el = fieldRef.current as any;
      el?.focus?.();
      if (behavior.caretToEndOnOpen && el instanceof HTMLDivElement) {
        setCaretToEnd(el);
      }
    });
  }, [behavior]);

  const focusOnClose = useCallback(() => {
    requestAnimationFrame(() => {
      btnRef.current?.focus();
      closingRef.current = false;
    });
  }, []);

  const closeWithValidationAndNormalize = useCallback((): boolean => {
    const liveVal =
      fieldRef.current instanceof HTMLDivElement ? getCEText(fieldRef.current) : text;

    if (typeof behavior.validate === 'function') {
      const res = behavior.validate(liveVal);
      if (hasValidationErrors(res)) {
        surfaceValidation(res);
        return false;
      }
    }

    normalizeOnCloseIfNeeded(liveVal);
    return true;
  }, [behavior, text, surfaceValidation, normalizeOnCloseIfNeeded]);

  const handleOpenChange = useCallback((next: boolean) => {
    if (!next) {
      const okToClose = closeWithValidationAndNormalize();
      if (!okToClose) return;
    }
    setOpen(next);
    onOpenChange?.(next);
    next ? focusOnOpen() : focusOnClose();
  }, [closeWithValidationAndNormalize, onOpenChange, focusOnOpen, focusOnClose]);

  // Measure control width to set min panel width
  useEffect(() => {
    let ro: ResizeObserver | undefined;
    const measure = () => {
      if (btnRef.current) setTriggerWidthPx(btnRef.current.offsetWidth);
    };
    if (open) {
      measure();
      if ('ResizeObserver' in window && btnRef.current) {
        ro = new ResizeObserver(measure);
        ro.observe(btnRef.current);
      }
    }
    return () => {
      if (ro && btnRef.current) ro.unobserve(btnRef.current);
    };
  }, [open]);

  // Close on outside click or Escape
  useEffect(() => {
    if (!open) return;

    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current && !panelRef.current.contains(t) && btnRef.current && !btnRef.current.contains(t)) {
        handleOpenChange(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleOpenChange(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, handleOpenChange]);

  // Validate on text change
  useEffect(() => {
    if (typeof behavior.validate !== 'function') {
      setError(null);
      setInvalidRanges(null);
      setKeyError(null);
      return;
    }
    const res = behavior.validate(text);
    setError(res?.error ?? null);
    setInvalidRanges(res?.invalidRanges ?? null);
    setKeyError(null);

    const hasInvalid = !!res?.error || (Array.isArray(res?.invalidRanges) && res!.invalidRanges!.length > 0);
    if (!hasInvalid && res?.validPayload !== undefined && onValidPayload) {
      onValidPayload(res.validPayload as ValidPayload);
    }
  }, [text, behavior.validate, onValidPayload, behavior]);

  // Keep helper text in view when error exists
  useEffect(() => {
    if (!open) return;
    const visibleMsg = keyError ?? error;
    if (!visibleMsg) return;
    requestAnimationFrame(() => {
      helperRef.current?.scrollIntoView({ block: 'nearest' });
    });
  }, [open, error, keyError]);

  const hasInvalidOverlay = Array.isArray(invalidRanges) && invalidRanges.length > 0;
  const htmlContent = useMemo(
    () => renderWithInvalids(text ?? '', hasInvalidOverlay ? invalidRanges! : null),
    [text, hasInvalidOverlay, invalidRanges]
  );

  // CE event handlers
  const handleBeforeInput = (e: React.FormEvent<FieldElement>) => {
    if (composingRef.current) return;
    behavior.beforeInput?.(e, api);
  };
  const handlePaste = (e: React.ClipboardEvent<FieldElement>) => {
    behavior.onPaste?.(e, api);
  };
  const handleDrop = (e: React.DragEvent<FieldElement>) => {
    behavior.onDrop?.(e, api);
  };
  const handleKeyDown = (e: React.KeyboardEvent<FieldElement>) => {
    if (closeOnEnter && e.key === 'Enter') {
      const composing = Boolean((e.nativeEvent as any)?.isComposing);
      if (!composing) {
        e.preventDefault();
        const liveValue =
          fieldRef.current instanceof HTMLDivElement ? getCEText(fieldRef.current) : text;

        if (typeof behavior.validate === 'function') {
          const res = behavior.validate(liveValue);
          if (hasValidationErrors(res)) {
            surfaceValidation(res);
            return;
          }
        }

        onSearch?.(liveValue);
        normalizeOnCloseIfNeeded(liveValue);

        setOpen(false);
        onOpenChange?.(false);
        focusOnClose();
        return;
      }
    }
    behavior.onKeyDown?.(e, api);
  };
  const handleBlur = () => {
    if (closingRef.current) return;
    if (typeof behavior.finalizeOnBlur !== 'function') return;
    const live = fieldRef.current instanceof HTMLDivElement ? getCEText(fieldRef.current) : text;
    const finalized = behavior.finalizeOnBlur(live);
    if (finalized !== text) {
      onChange ? onChange(finalized) : setInternalValue(finalized);
    }
  };
  const handleInput = () => {
    if (fieldRef.current instanceof HTMLDivElement) {
      api.setText(getCEText(fieldRef.current));
    }
  };

  /**
   * Only use innerHTML when INVALID (to show red spans).
   * While VALID: never touch innerHTML — set textContent and don't move the caret.
   */
  useLayoutEffect(() => {
    if (!open) return;
    const node = fieldRef.current;
    if (!(node instanceof HTMLDivElement)) return;

    const el = node as HTMLDivElement;

    if (hasInvalidOverlay) {
      const nextHtml = htmlContent;
      if (el.innerHTML !== nextHtml) {
        const idx = getSelectionIndex(el);
        el.innerHTML = nextHtml;
        setCaretAtIndex(el, idx);
      }
    } else {
      const plain = text ?? '';
      if (el.textContent !== plain) {
        const idx = getSelectionIndex(el);
        el.textContent = plain;
        setCaretAtIndex(el, idx);
      }
    }
  }, [open, htmlContent, hasInvalidOverlay, text]);

  const ariaInvalid = (keyError ?? error) ? true : undefined;
  const visibleMessage = keyError ?? error;

  return (
    <S.Root className={className}>
      {open && (!!error || (invalidRanges && invalidRanges.length > 0)) && (
        <S.Backdrop onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} />
      )}

      <S.Button
        ref={btnRef}
        className={disabled ? 'is-disabled' : undefined}
        onClick={() => !disabled && handleOpenChange(!open)}
        disabled={disabled}
        title={((text ?? '').trim().length
          ? getResource(resources, 'ACCESS_OVERVIEW_SEARCH_WITH_TEXT_TITLE', (text ?? '').trim())
          : label)}
        controlWidth={controlWidth}
        controlMaxWidth={controlMaxWidth}
        controlHeight={controlHeight}
        iconSize={iconSize}
      >
        <S.ButtonBody>
          <S.ButtonText
            isPlaceholder={!((text ?? '').trim().length)}
            title={useMemo(() => {
              const t = (text ?? '').trim();
              return t.length ? t : label;
            }, [text, label])}
          >
            {(text ?? '').trim().length ? (text ?? '').trim() : label}
          </S.ButtonText>
        </S.ButtonBody>

        <S.SearchIcon iconSize={iconSize}>
          <svg viewBox="0 0 24 24" width="1em" height="1em" focusable="false" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </S.SearchIcon>
      </S.Button>

      {open && (
        <S.Panel
          ref={panelRef}
          id={popupId}
          role="dialog"
          minW={triggerWidthPx}
          prefMinW={panelMinWidth}
          maxW={panelMaxWidth}
          panelMaxHeight={panelMaxHeight}
        >
          <S.Search>
            <S.SearchFieldWrapper>
              {behavior.fieldKind === 'textarea' ? (
                <S.Editable
                  ref={fieldRef as React.RefObject<HTMLDivElement>}
                  role="textbox"
                  aria-multiline="true"
                  aria-invalid={ariaInvalid}
                  contentEditable
                  data-placeholder={searchPlaceholder || ''}
                  onInput={handleInput}
                  onBeforeInput={handleBeforeInput as any}
                  onPaste={handlePaste as any}
                  onDrop={handleDrop as any}
                  onKeyDown={handleKeyDown as any}
                  onBlur={handleBlur}
                  style={editableStyle}   // CE gets rows + maxHeight + scrollbar
                />
              ) : (
                <input
                  value={text}
                  onChange={(e) => api.setText((e.target as any).value)}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown as any}
                  placeholder={searchPlaceholder}
                  maxLength={behavior.maxLengthAttr}
                  inputMode={behavior.inputMode}
                  onBeforeInput={handleBeforeInput as any}
                  onPaste={handlePaste as any}
                  onDrop={handleDrop as any}
                  aria-invalid={ariaInvalid}
                  ref={fieldRef as React.RefObject<HTMLInputElement>}
                  type="text"
                />
              )}
            </S.SearchFieldWrapper>

            {showToolbarClear && (
              <S.ClearButton
                type="button"
                onClick={() => {
                  api.setText('');
                  setKeyError(null);
                  setError(null);
                  setInvalidRanges(null);
                  requestAnimationFrame(() => {
                    const el = fieldRef.current as any;
                    el?.focus?.();
                    if (el instanceof HTMLDivElement) setCaretToEnd(el);
                  });
                }}
                disabled={!(text?.length)}
                title={getResource(resources, 'ACCESS_OVERVIEW_CLEAR_SEARCH')}
              >
                {clearLabel ?? 'Clear'}
              </S.ClearButton>
            )}
          </S.Search>

          {visibleMessage && (
            <S.HelperText ref={helperRef} role="alert">
              {visibleMessage}
            </S.HelperText>
          )}
        </S.Panel>
      )}
    </S.Root>
  );
}
